import React from 'react';
import styled from '@emotion/native';
import { View } from 'react-native';
import { useQuery } from '@apollo/client';
import { ActivityIndicator } from 'react-native-paper';
import { format } from 'date-fns';
import {
  GET_ADMIN_BOOKINGS,
  GET_ADMIN_CONVERSATIONS,
  GET_ADMIN_OVERVIEW,
  GET_ADMIN_STYLISTS,
  GET_ADMIN_USERS,
} from '@/shared/queries/admin';
import {
  AdminBookingFilter,
  AdminCalendarState,
  AdminStylistStatus,
  QueryAdminBookingsArgs,
  QueryAdminUsersArgs,
  QueryResult,
  UserType,
} from '@/shared/types/generated';
import { green } from '@/shared/styles/colors';
import {
  ActionLink,
  Avatar,
  ErrorNote,
  ErrorState,
  Grid,
  Headline,
  MetricCard,
  MiniBars,
  Muted,
  Panel,
  Pill,
  RowCard,
  SectionTitle,
  Small,
  Spread,
  Stack,
  Strong,
  Tone,
} from './AdminUI';
import {
  ago,
  bookableTone,
  money,
  plural,
  sessionLabel,
  stylistState,
  toDate,
} from './format';
import { byStylistsFirst, isWaiting, roleName } from './messages';
import type { OpenTab } from './AdminDashboard';

const PREVIEW_LIMIT = 5;

const OverviewPanel = ({
  isDesktop,
  onOpenTab,
}: {
  isDesktop: boolean;
  onOpenTab: OpenTab;
}) => {
  // Each query can fail on its own; a failure must never read as "all clear"
  const overviewQuery = useQuery<QueryResult<'adminOverview'>>(
    GET_ADMIN_OVERVIEW,
    { fetchPolicy: 'network-only', notifyOnNetworkStatusChange: true },
  );
  const stylistQuery = useQuery<QueryResult<'adminStylists'>>(
    GET_ADMIN_STYLISTS,
    { fetchPolicy: 'network-only', notifyOnNetworkStatusChange: true },
  );
  const conversationQuery = useQuery<QueryResult<'adminConversations'>>(
    GET_ADMIN_CONVERSATIONS,
    { fetchPolicy: 'network-only', notifyOnNetworkStatusChange: true },
  );
  const upcomingQuery = useQuery<
    QueryResult<'adminBookings'>,
    QueryAdminBookingsArgs
  >(GET_ADMIN_BOOKINGS, {
    variables: { filter: AdminBookingFilter.Upcoming },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const signupsQuery = useQuery<QueryResult<'adminUsers'>, QueryAdminUsersArgs>(
    GET_ADMIN_USERS,
    {
      variables: { days: 30 },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    },
  );

  const overview = overviewQuery.data?.adminOverview;
  const unbookable = (stylistQuery.data?.adminStylists ?? []).filter(
    (s) =>
      !s.isTestUser &&
      (s.status === AdminStylistStatus.NoCalendar ||
        s.status === AdminStylistStatus.NoUpcomingHours),
  );
  const waiting = (conversationQuery.data?.adminConversations ?? [])
    .filter(isWaiting)
    .sort(byStylistsFirst);

  if (overviewQuery.loading && !overview) {
    return (
      <ActivityIndicator size="large" color={green} style={{ marginTop: 80 }} />
    );
  }

  if (overviewQuery.error && !overview) {
    return (
      <ErrorState
        what="the overview"
        onRetry={() => {
          overviewQuery.refetch();
          stylistQuery.refetch();
          conversationQuery.refetch();
        }}
      />
    );
  }

  if (!overview) {
    return null;
  }

  const blocked = overview.listedStylists - overview.bookableStylists;
  const byCalendar = (state: AdminCalendarState) =>
    unbookable.filter(
      (s) => s.status === AdminStylistStatus.NoCalendar && s.calendar === state,
    ).length;
  const noHours = unbookable.filter(
    (s) => s.status === AdminStylistStatus.NoUpcomingHours,
  ).length;
  const stylistsIgnoringClients = waiting.filter(
    (c) => c.recipient.type === UserType.Stylist,
  ).length;

  const needsYou = [
    blocked > 0 && {
      key: 'stylists',
      tone: 'problem' as Tone,
      text: `${plural(blocked, 'stylist')} can’t be booked`,
      detail: [
        byCalendar(AdminCalendarState.NeedsReconnect) &&
          `${byCalendar(AdminCalendarState.NeedsReconnect)} need to reconnect their calendar`,
        byCalendar(AdminCalendarState.NotConnected) &&
          `${byCalendar(AdminCalendarState.NotConnected)} haven’t connected one`,
        byCalendar(AdminCalendarState.NoBookingCalendar) &&
          `${byCalendar(AdminCalendarState.NoBookingCalendar)} haven’t picked a booking calendar`,
        noHours && `${noHours} have no open hours`,
      ]
        .filter(Boolean)
        .join(' · '),
      actionText: 'See stylists',
      onPress: () => onOpenTab('stylists'),
    },
    overview.pendingRequests > 0 && {
      key: 'requests',
      tone: 'attention' as Tone,
      text: `${plural(overview.pendingRequests, 'in-person request')} waiting for a reply`,
      detail: 'The stylist needs to accept or decline.',
      actionText: 'See requests',
      onPress: () => onOpenTab('bookings', 'needs-reply'),
    },
    (overview.unopenedConversations ?? 0) > 0 && {
      key: 'messages',
      tone: 'attention' as Tone,
      text: `${plural(overview.unopenedConversations ?? 0, 'chat')} unopened for over a day`,
      detail: stylistsIgnoringClients
        ? `${plural(stylistsIgnoringClients, 'stylist')} haven’t opened a client’s message`
        : 'All of them are clients who haven’t opened a stylist’s message',
      actionText: 'See messages',
      onPress: () => onOpenTab('messages'),
    },
  ].filter(Boolean) as React.ComponentProps<typeof Headline>['items'];

  const weeks = overview.weeks;
  const weekLabel = (text: string) =>
    `${text}, last ${weeks.length} weeks (from ${format(new Date(`${weeks[0]?.weekStart}T12:00:00`), 'MMM d')})`;

  return (
    <View style={{ gap: 28 }}>
      <Headline
        title={
          needsYou.length === 0
            ? 'All clear'
            : `${plural(needsYou.length, 'thing')} need${needsYou.length === 1 ? 's' : ''} you`
        }
        items={needsYou}
        allClear="Every listed stylist can be booked, no requests are waiting, and nobody is sitting on an unopened message."
      />

      <Grid columns={isDesktop ? 3 : 1}>
        <MetricCard
          label="Bookable stylists"
          value={`${overview.bookableStylists} of ${overview.listedStylists}`}
          sub="Have hours clients can book in the next 4 weeks"
          tone={bookableTone(
            overview.bookableStylists,
            overview.listedStylists,
          )}
          onPress={() => onOpenTab('stylists')}
        />
        <MetricCard
          label="Requests waiting"
          value={String(overview.pendingRequests)}
          sub="In-person requests to accept or decline"
          tone={overview.pendingRequests > 0 ? 'attention' : 'neutral'}
          onPress={() => onOpenTab('bookings', 'needs-reply')}
        />
        <MetricCard
          label="Upcoming sessions"
          value={String(overview.upcomingBookings)}
          sub="Confirmed and still ahead"
          onPress={() => onOpenTab('bookings')}
        />
        <MetricCard
          label="Completed this month"
          value={String(overview.completedThisMonth)}
          compare={`vs ${overview.completedLastMonthToDate} this time last month`}
          bars={
            <MiniBars
              values={weeks.map((w) => w.completed)}
              label={weekLabel('Sessions')}
            />
          }
        />
        <MetricCard
          label="EMME fees this month"
          value={money(overview.platformFeeThisMonthCents)}
          sub={`Clients paid ${money(overview.revenueThisMonthCents)} in total`}
          compare={`vs ${money(overview.platformFeeLastMonthToDateCents)} this time last month`}
          bars={
            <MiniBars
              values={weeks.map((w) => w.platformFeeCents)}
              label={weekLabel('Fees')}
              describe={money}
            />
          }
        />
        <MetricCard
          label="New clients this month"
          value={String(overview.newClientsThisMonth)}
          compare={`vs ${overview.newClientsLastMonthToDate} this time last month`}
          bars={
            <MiniBars
              values={weeks.map((w) => w.newClients)}
              label={weekLabel('Sign-ups')}
            />
          }
          onPress={() => onOpenTab('users')}
        />
      </Grid>

      <Grid columns={isDesktop ? 2 : 1} gap={20} stretch={false}>
        <Panel style={{ flex: 1 }}>
          <Spread style={{ marginBottom: 12 }}>
            <SectionTitle style={{ marginBottom: 0 }}>
              Can’t be booked
              {stylistQuery.data ? ` (${unbookable.length})` : ''}
            </SectionTitle>
            <ActionLink
              text={
                unbookable.length > PREVIEW_LIMIT
                  ? `See all ${unbookable.length}`
                  : 'See stylists'
              }
              onPress={() => onOpenTab('stylists')}
            />
          </Spread>
          {stylistQuery.loading && !stylistQuery.data ? (
            <ActivityIndicator color={green} />
          ) : stylistQuery.error && !stylistQuery.data ? (
            <ErrorNote what="stylists" onRetry={() => stylistQuery.refetch()} />
          ) : unbookable.length === 0 ? (
            <Muted>Every listed stylist can be booked.</Muted>
          ) : (
            unbookable.slice(0, PREVIEW_LIMIT).map((stylist) => {
              const state = stylistState(stylist);
              return (
                <RowCard key={stylist.stylistId} isDesktop>
                  <Avatar
                    uri={stylist.profilePicUrl}
                    name={stylist.name}
                    size={36}
                  />
                  <Stack style={{ flex: 1 }}>
                    <Strong>{stylist.name}</Strong>
                    <Small numberOfLines={2}>{state.reason}</Small>
                  </Stack>
                  <Pill text={state.label} tone={state.tone} />
                </RowCard>
              );
            })
          )}
        </Panel>

        <Panel style={{ flex: 1 }}>
          <Spread style={{ marginBottom: 12 }}>
            <SectionTitle style={{ marginBottom: 0 }}>
              Unopened messages
              {conversationQuery.data ? ` (${waiting.length})` : ''}
            </SectionTitle>
            <ActionLink
              text="See messages"
              onPress={() => onOpenTab('messages')}
            />
          </Spread>
          {conversationQuery.loading && !conversationQuery.data ? (
            <ActivityIndicator color={green} />
          ) : conversationQuery.error && !conversationQuery.data ? (
            <ErrorNote
              what="messages"
              onRetry={() => conversationQuery.refetch()}
            />
          ) : waiting.length === 0 ? (
            <Muted>Nobody has left a message unopened for over a day.</Muted>
          ) : (
            waiting.slice(0, PREVIEW_LIMIT).map((conversation) => (
              <RowCard
                key={`${conversation.channelUrl}-${conversation.recipient.userId}`}
                isDesktop
              >
                <Stack style={{ flex: 1 }}>
                  <Strong>
                    {conversation.recipient.name}{' '}
                    <Small>({roleName(conversation.recipient)})</Small>
                  </Strong>
                  <Small numberOfLines={1}>
                    Hasn’t opened a message from {conversation.sender.name} (
                    {roleName(conversation.sender)})
                  </Small>
                </Stack>
                <Small>{ago(conversation.lastMessageAt)}</Small>
              </RowCard>
            ))
          )}
          {overview.staleUnopenedConversations ? (
            <Small style={{ marginTop: 6 }}>
              Plus {overview.staleUnopenedConversations} unopened for over 30
              days, under Messages.
            </Small>
          ) : null}
        </Panel>

        <Panel style={{ flex: 1 }}>
          <Spread style={{ marginBottom: 12 }}>
            <SectionTitle style={{ marginBottom: 0 }}>Coming up</SectionTitle>
            <ActionLink
              text="See bookings"
              onPress={() => onOpenTab('bookings')}
            />
          </Spread>
          {upcomingQuery.loading && !upcomingQuery.data ? (
            <ActivityIndicator color={green} />
          ) : upcomingQuery.error && !upcomingQuery.data ? (
            <ErrorNote
              what="bookings"
              onRetry={() => upcomingQuery.refetch()}
            />
          ) : (upcomingQuery.data?.adminBookings ?? []).length === 0 ? (
            <Muted>No confirmed sessions coming up.</Muted>
          ) : (
            (upcomingQuery.data?.adminBookings ?? [])
              .slice(0, PREVIEW_LIMIT)
              .map((booking) => (
                <RowCard key={booking.id} isDesktop>
                  <DateTag>
                    <Small style={{ color: green, fontWeight: 'bold' }}>
                      {format(toDate(booking.startTime), 'EEE MMM d')}
                    </Small>
                    <Small>{format(toDate(booking.startTime), 'h:mm a')}</Small>
                  </DateTag>
                  <Stack style={{ flex: 1 }}>
                    <Strong numberOfLines={1}>
                      {booking.clientName} with {booking.stylistName}
                    </Strong>
                    <Small>
                      {sessionLabel(booking)}
                      {booking.isTest ? ' · test' : ''}
                    </Small>
                  </Stack>
                </RowCard>
              ))
          )}
        </Panel>

        <Panel style={{ flex: 1 }}>
          <Spread style={{ marginBottom: 12 }}>
            <SectionTitle style={{ marginBottom: 0 }}>
              New sign-ups
            </SectionTitle>
            <ActionLink text="See users" onPress={() => onOpenTab('users')} />
          </Spread>
          {signupsQuery.loading && !signupsQuery.data ? (
            <ActivityIndicator color={green} />
          ) : signupsQuery.error && !signupsQuery.data ? (
            <ErrorNote what="sign-ups" onRetry={() => signupsQuery.refetch()} />
          ) : (signupsQuery.data?.adminUsers.users ?? []).length === 0 ? (
            <Muted>Nobody signed up in the last 30 days.</Muted>
          ) : (
            (signupsQuery.data?.adminUsers.users ?? [])
              .slice(0, PREVIEW_LIMIT)
              .map((user) => (
                <RowCard key={user.userId} isDesktop>
                  <Avatar uri={user.profilePicUrl} name={user.name} size={36} />
                  <Stack style={{ flex: 1 }}>
                    <Strong numberOfLines={1}>{user.name}</Strong>
                    <Small>
                      {user.type === UserType.Stylist ? 'Stylist' : 'Client'} ·
                      joined {ago(user.createdAt)}
                    </Small>
                  </Stack>
                </RowCard>
              ))
          )}
        </Panel>
      </Grid>
    </View>
  );
};

const DateTag = styled.View({
  width: 96,
  gap: 2,
});

export default OverviewPanel;
